import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import BreadcrumbNav from '@/components/feature/BreadcrumbNav';
import { categories, comics } from '@/mocks/comics';

const catCoverImages = [
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20cover%20art%20style%2C%20beautiful%20female%20character%20portrait%2C%20soft%20lighting%2C%20clean%20composition%2C%20warm%20toned%20background%2C%20digital%20illustration%2C%20romantic%20and%20elegant%20aesthetic%2C%20manga%20inspired%20artwork&width=800&height=1140&seq=comic-cover-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20fantasy%20cover%20art%2C%20young%20male%20character%20in%20magic%20academy%20uniform%2C%20glowing%20magic%20effects%2C%20dark%20blue%20and%20gold%20color%20scheme%2C%20digital%20illustration%2C%20anime%20inspired%20style%2C%20clean%20composition&width=800&height=1140&seq=comic-cover-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20action%20cover%2C%20male%20hunter%20character%20with%20dark%20outfit%2C%20shadowy%20urban%20background%2C%20dramatic%20lighting%2C%20cool%20tone%20color%20palette%2C%20digital%20art%2C%20webtoon%20style%2C%20powerful%20pose&width=800&height=1140&seq=comic-cover-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20drama%20cover%2C%20young%20female%20character%20in%20winter%20setting%2C%20snowy%20landscape%20background%2C%20soft%20emotional%20expression%2C%20cool%20blue%20and%20white%20color%20scheme%2C%20watercolor%20inspired%20art%2C%20gentle%20atmosphere&width=800&height=1140&seq=comic-cover-04&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20comedy%20cover%2C%20office%20workers%20in%20funny%20situation%2C%20bright%20colorful%20palette%2C%20cartoonish%20expression%2C%20clean%20modern%20office%20background%2C%20digital%20illustration%2C%20lighthearted%20and%20playful%20mood&width=800&height=1140&seq=comic-cover-05&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20thriller%20cover%2C%20detective%20character%20in%20dark%20alley%2C%20neon%20signs%20glowing%2C%20rainy%20night%20atmosphere%2C%20high%20contrast%20lighting%2C%20dark%20noir%20style%2C%20suspenseful%20mood%2C%20digital%20art&width=800&height=1140&seq=comic-cover-06&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20dance%20cover%2C%20female%20dancer%20in%20elegant%20pose%2C%20stage%20spotlight%20background%2C%20flowing%20fabric%20movement%2C%20warm%20golden%20lighting%2C%20graceful%20composition%2C%20digital%20illustration%2C%20artistic%20style&width=800&height=1140&seq=comic-cover-07&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20fantasy%20cover%2C%20demon%20king%20character%20in%20school%20uniform%2C%20dark%20aura%20surrounding%2C%20modern%20school%20background%2C%20purple%20and%20black%20color%20scheme%2C%20dramatic%20composition%2C%20webtoon%20style&width=800&height=1140&seq=comic-cover-08&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20romance%20cover%2C%20couple%20on%20jeju%20island%20beach%2C%20ocean%20and%20green%20fields%20background%2C%20warm%20sunset%20lighting%2C%20sweet%20atmosphere%2C%20digital%20illustration%2C%20soft%20pastel%20colors&width=800&height=1140&seq=comic-cover-09&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20historical%20martial%20arts%20cover%2C%20swordsman%20in%20traditional%20Korean%20hanbok%2C%20ancient%20palace%20background%2C%20dramatic%20wind%20effects%2C%20ink%20brush%20artistic%20style%2C%20powerful%20and%20elegant&width=800&height=1140&seq=comic-cover-10&orientation=portrait',
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'romance');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug) || categories[0],
    [activeSlug],
  );

  const filteredComics = useMemo(
    () => comics.filter((c) => c.category === activeSlug),
    [activeSlug],
  );

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 韩漫在线`,
    description: currentCategory.description,
    url: `http://wxctif.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫在线',
      url: 'http://wxctif.com/',
    },
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}韩漫 - ${currentCategory.nameEn}`}
        description={`韩漫在线${currentCategory.name}分类，${currentCategory.description}。收录热门${currentCategory.name}韩国漫画，高清画质免费下拉式阅读。`}
        keywords={`${currentCategory.name}韩漫,${currentCategory.name}漫画,韩国${currentCategory.name}漫画,${currentCategory.nameEn} manhwa`}
        canonicalUrl={`http://wxctif.com/category/${activeSlug}`}
        schema={categorySchema}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <BreadcrumbNav
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/romance' },
                { label: currentCategory.name },
              ]}
            />
          </div>
        </div>

        <section className="py-8 md:py-12">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="mb-8 md:mb-10">
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-3">
                  {currentCategory.name}韩漫
                </h1>
                <p className="text-foreground-500 text-sm md:text-base max-w-2xl">
                  {currentCategory.description}。共收录
                  <strong className="text-primary-500 font-semibold">{filteredComics.length}</strong>
                  部作品。
                </p>
              </div>

              <div className="flex items-center gap-2 md:gap-3 mb-8 overflow-x-auto pb-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
                {filteredComics.map((comic, i) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.slug}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg mb-3 aspect-[7/10] bg-background-100">
                      <img
                        src={catCoverImages[parseInt(comic.id) - 1] || catCoverImages[0]}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      {comic.status === 'ongoing' && (
                        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-primary-500 text-white text-[10px] font-medium">
                          连载
                        </span>
                      )}
                      {comic.status === 'completed' && (
                        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-medium">
                          完结
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <div className="flex items-center gap-1 mt-1 text-xs text-foreground-400">
                      <span>{comic.author}</span>
                      <span>·</span>
                      <span>{comic.chapters}话</span>
                    </div>
                    <div className="flex items-center gap-1 mt-0.5 text-xs">
                      <i className="ri-star-fill text-yellow-500 text-[10px]"></i>
                      <span className="text-foreground-500">{comic.rating}</span>
                    </div>
                  </Link>
                ))}
              </div>

              {filteredComics.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-book-open-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无作品，敬请期待</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}